import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { appointmentsApi } from '../../lib/api/appointments'
import { skillsApi } from '../../lib/api/skills'
import { teamMembersApi } from '../../lib/api/teamMembers'
import { customersApi } from '../../lib/api/customers'

interface SchedulingWizardProps {
  isOpen: boolean
  onClose: () => void
  selectedDate?: string
}

export function SchedulingWizard({ isOpen, onClose, selectedDate }: SchedulingWizardProps) {
  const queryClient = useQueryClient()
  const [step, setStep] = useState(1)
  const [formData, setFormData] = useState({
    skill_id: 0,
    team_member_id: 0,
    date: selectedDate || new Date().toISOString().split('T')[0],
    start_time: '',
    end_time: '',
    customer_id: 0,
    notes: '',
  })
  const [newCustomer, setNewCustomer] = useState({ name: '', email: '', phone: '' })
  const [isCreatingCustomer, setIsCreatingCustomer] = useState(false)

  // Fetch all skills
  const { data: skillsData } = useQuery({
    queryKey: ['allSkills'],
    queryFn: skillsApi.getAll,
  })

  // Fetch team members with selected skill
  const { data: teamMembersData } = useQuery({
    queryKey: ['teamMembersBySkill', formData.skill_id],
    queryFn: () => teamMembersApi.getAll({ skill_id: formData.skill_id }),
    enabled: formData.skill_id > 0,
  })

  // Fetch availability for selected team member and date
  const { data: availabilityData } = useQuery({
    queryKey: ['availability', formData.team_member_id, formData.date],
    queryFn: () => appointmentsApi.getAvailability(formData.team_member_id, formData.date),
    enabled: formData.team_member_id > 0 && !!formData.date,
  })

  // Fetch customers
  const { data: customersData } = useQuery({
    queryKey: ['customers'],
    queryFn: () => customersApi.getAll(),
  })

  // Create customer mutation
  const createCustomerMutation = useMutation({
    mutationFn: customersApi.create,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['customers'] })
      setFormData({ ...formData, customer_id: data.customer.id })
      setIsCreatingCustomer(false)
    },
  })

  // Create appointment mutation
  const createAppointmentMutation = useMutation({
    mutationFn: appointmentsApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['appointments'] })
      handleClose()
    },
  })

  const handleClose = () => {
    setStep(1)
    setFormData({
      skill_id: 0,
      team_member_id: 0,
      date: selectedDate || new Date().toISOString().split('T')[0],
      start_time: '',
      end_time: '',
      customer_id: 0,
      notes: '',
    })
    setNewCustomer({ name: '', email: '', phone: '' })
    setIsCreatingCustomer(false)
    onClose()
  }

  const handleNext = () => {
    if (step < 4) setStep(step + 1)
  }

  const handleBack = () => {
    if (step > 1) setStep(step - 1)
  }

  const handleSubmit = () => {
    if (isCreatingCustomer) {
      createCustomerMutation.mutate(newCustomer)
    } else {
      createAppointmentMutation.mutate(formData)
    }
  }

  const handleCreateCustomer = () => {
    if (newCustomer.name) {
      createCustomerMutation.mutate(newCustomer)
    }
  }

  // Generate time slots (9 AM to 6 PM, 30-minute intervals)
  const generateTimeSlots = () => {
    const slots = []
    for (let hour = 9; hour < 18; hour++) {
      for (let minute = 0; minute < 60; minute += 30) {
        const timeStr = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`
        slots.push(timeStr)
      }
    }
    return slots
  }

  const isTimeSlotAvailable = (time: string) => {
    if (!availabilityData) return true
    
    const [hours, minutes] = time.split(':').map(Number)
    const slotTime = hours * 60 + minutes
    
    return !availabilityData.booked_slots.some((slot) => {
      const [startHours, startMinutes] = slot.start_time.split(':').map(Number)
      const [endHours, endMinutes] = slot.end_time.split(':').map(Number)
      const startTime = startHours * 60 + startMinutes
      const endTime = endHours * 60 + endMinutes
      
      return slotTime >= startTime && slotTime < endTime
    })
  }

  // Group skills by category
  const skillsByCategory = skillsData?.skills.reduce((acc, skill) => {
    if (!acc[skill.category]) {
      acc[skill.category] = []
    }
    acc[skill.category].push(skill)
    return acc
  }, {} as Record<string, typeof skillsData.skills>) || {}

  if (!isOpen) return null

  return (
    <dialog className="modal modal-open">
      <div className="modal-box max-w-2xl">
        <h3 className="font-bold text-lg mb-4">
          Schedule Appointment
        </h3>

        {/* Progress Steps */}
        <ul className="steps steps-horizontal w-full mb-6">
          <li className={`step ${step >= 1 ? 'step-accent' : ''}`}>Skill</li>
          <li className={`step ${step >= 2 ? 'step-accent' : ''}`}>Provider</li>
          <li className={`step ${step >= 3 ? 'step-accent' : ''}`}>Time</li>
          <li className={`step ${step >= 4 ? 'step-accent' : ''}`}>Client</li>
        </ul>

        {/* Error Display */}
        {createAppointmentMutation.error && (
          <div className="alert alert-error mb-4">
            <span>{(createAppointmentMutation.error as any)?.response?.data?.errors?.join(', ') || 'An error occurred'}</span>
          </div>
        )}

        {/* Step 1: Select Skill */}
        {step === 1 && (
          <div className="space-y-4">
            <p className="text-sm text-base-content/70">Select the service type</p>
            <div className="space-y-4 max-h-96 overflow-y-auto">
              {Object.entries(skillsByCategory).map(([category, skills]) => (
                <div key={category}>
                  <h4 className="font-semibold text-sm mb-2">{category}</h4>
                  <div className="grid grid-cols-2 gap-2">
                    {skills.map((skill) => (
                      <button
                        key={skill.id}
                        className={`btn btn-sm justify-start ${formData.skill_id === skill.id ? 'btn-accent text-white' : 'btn-outline'}`}
                        onClick={() => setFormData({ ...formData, skill_id: skill.id, team_member_id: 0 })}
                      >
                        {skill.name}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Step 2: Select Team Member */}
        {step === 2 && (
          <div className="space-y-4">
            <p className="text-sm text-base-content/70">Select a provider with this skill</p>
            {teamMembersData && teamMembersData.team_members.length > 0 ? (
              <div className="space-y-2">
                {teamMembersData.team_members.map((member) => (
                  <button
                    key={member.id}
                    className={`btn btn-block justify-start ${formData.team_member_id === member.id ? 'btn-accent text-white' : 'btn-outline'}`}
                    onClick={() => setFormData({ ...formData, team_member_id: member.id })}
                  >
                    <div className="flex flex-col items-start">
                      <span className="font-semibold">{member.user.email}</span>
                      <div className="flex gap-1 flex-wrap">
                        {member.user.skills?.map((skill) => (
                          <span key={skill.id} className="badge badge-xs badge-outline">{skill.name}</span>
                        ))}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            ) : (
              <div className="alert alert-warning">
                <span>No team members available with this skill</span>
              </div>
            )}
          </div>
        )}

        {/* Step 3: Select Date and Time */}
        {step === 3 && (
          <div className="space-y-4">
            <div className="form-control">
              <label className="label">
                <span className="label-text">Date</span>
              </label>
              <input
                type="date"
                className="input input-bordered"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                min={new Date().toISOString().split('T')[0]}
              />
            </div>

            <div className="form-control">
              <label className="label">
                <span className="label-text">Start Time</span>
              </label>
              <select
                className="select select-bordered"
                value={formData.start_time}
                onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
              >
                <option value="">Select start time</option>
                {generateTimeSlots().map((time) => (
                  <option 
                    key={time} 
                    value={time}
                    disabled={!isTimeSlotAvailable(time)}
                  >
                    {time} {!isTimeSlotAvailable(time) ? '(Booked)' : ''}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-control">
              <label className="label">
                <span className="label-text">End Time</span>
              </label>
              <select
                className="select select-bordered"
                value={formData.end_time}
                onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
              >
                <option value="">Select end time</option>
                {generateTimeSlots().filter(time => time > formData.start_time).map((time) => (
                  <option key={time} value={time}>{time}</option>
                ))}
              </select>
            </div>

            <div className="form-control">
              <label className="label">
                <span className="label-text">Notes (Optional)</span>
              </label>
              <textarea
                className="textarea textarea-bordered"
                placeholder="Any special notes or requirements"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              />
            </div>
          </div>
        )}

        {/* Step 4: Select/Create Customer */}
        {step === 4 && (
          <div className="space-y-4">
            {!isCreatingCustomer ? (
              <>
                <div className="flex justify-between items-center">
                  <p className="text-sm text-base-content/70">Select existing client or create new</p>
                  <button
                    className="btn btn-sm btn-accent text-white"
                    onClick={() => setIsCreatingCustomer(true)}
                  >
                    + New Client
                  </button>
                </div>

                <div className="form-control">
                  <select
                    className="select select-bordered"
                    value={formData.customer_id}
                    onChange={(e) => setFormData({ ...formData, customer_id: Number(e.target.value) })}
                  >
                    <option value={0}>Select a client</option>
                    {customersData?.customers.map((customer) => (
                      <option key={customer.id} value={customer.id}>
                        {customer.name} {customer.email ? `(${customer.email})` : ''}
                      </option>
                    ))}
                  </select>
                </div>
              </>
            ) : (
              <>
                <div className="flex justify-between items-center">
                  <p className="text-sm font-semibold">Create New Client</p>
                  <button
                    className="btn btn-sm btn-ghost"
                    onClick={() => setIsCreatingCustomer(false)}
                  >
                    Cancel
                  </button>
                </div>

                <div className="form-control">
                  <label className="label">
                    <span className="label-text">Name *</span>
                  </label>
                  <input
                    type="text"
                    className="input input-bordered"
                    value={newCustomer.name}
                    onChange={(e) => setNewCustomer({ ...newCustomer, name: e.target.value })}
                    placeholder="Client name"
                  />
                </div>

                <div className="form-control">
                  <label className="label">
                    <span className="label-text">Email</span>
                  </label>
                  <input
                    type="email"
                    className="input input-bordered"
                    value={newCustomer.email}
                    onChange={(e) => setNewCustomer({ ...newCustomer, email: e.target.value })}
                    placeholder="email@example.com"
                  />
                </div>

                <div className="form-control">
                  <label className="label">
                    <span className="label-text">Phone</span>
                  </label>
                  <input
                    type="tel"
                    className="input input-bordered"
                    value={newCustomer.phone}
                    onChange={(e) => setNewCustomer({ ...newCustomer, phone: e.target.value })}
                    placeholder="(555) 123-4567"
                  />
                </div>
              </>
            )}

            {/* Appointment Summary */}
            <div className="bg-accent text-white rounded-lg p-4">
              <div className="text-sm">
                <p className="font-semibold mb-1">Appointment Summary:</p>
                <p>Service: {skillsData?.skills.find(s => s.id === formData.skill_id)?.name}</p>
                <p>Provider: {teamMembersData?.team_members.find(m => m.id === formData.team_member_id)?.user.email}</p>
                <p>Date: {formData.date}</p>
                <p>Time: {formData.start_time} - {formData.end_time}</p>
              </div>
            </div>
          </div>
        )}

        {/* Modal Actions */}
        <div className="modal-action">
          <button className="btn" onClick={handleClose}>
            Cancel
          </button>
          {step > 1 && (
            <button className="btn btn-ghost" onClick={handleBack}>
              Back
            </button>
          )}
          {step < 4 ? (
            <button
              className="btn btn-accent text-white"
              onClick={handleNext}
              disabled={
                (step === 1 && formData.skill_id === 0) ||
                (step === 2 && formData.team_member_id === 0) ||
                (step === 3 && (!formData.date || !formData.start_time || !formData.end_time))
              }
            >
              Next
            </button>
          ) : (
            <button
              className="btn btn-accent text-white"
              onClick={handleSubmit}
              disabled={
                (!isCreatingCustomer && formData.customer_id === 0) ||
                (isCreatingCustomer && !newCustomer.name) ||
                createAppointmentMutation.isPending ||
                createCustomerMutation.isPending
              }
            >
              {createAppointmentMutation.isPending || createCustomerMutation.isPending
                ? 'Creating...'
                : 'Confirm Booking'}
            </button>
          )}
        </div>
      </div>
      <form method="dialog" className="modal-backdrop">
        <button onClick={handleClose}>close</button>
      </form>
    </dialog>
  )
}
